import clsx from "clsx";
import { motion } from "framer-motion";
import React, { FC, useEffect, useRef, useState } from "react";

import { useWindowSize } from "../../../hooks/use-window-size";

export interface TabProps {
  key: string;
  label: string;
}

export interface MaterialsSliderTabsProps {
  tabs: TabProps[];
  activeTabKey?: string;
  showSlide: (key: string) => void;
  isVertical?: boolean;
  className?: string;
  color?: "light" | "dark";
}

export const MaterialsSliderTabs: FC<MaterialsSliderTabsProps> = ({
  tabs,
  activeTabKey,
  showSlide,
  isVertical = true,
  className,
  color = "dark",
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const activeTab = tabs.find(({ key }) => key === activeTabKey);
  const windowSize = useWindowSize();

  const [activeTabRect, setActiveTabRect] = useState<Pick<
    DOMRect,
    "top" | "left" | "width" | "height"
  > | null>(null);
  useEffect(() => {
    const activeElemQuery = rootRef.current?.querySelectorAll(".active div");
    if (activeElemQuery?.length) {
      const activeRect = activeElemQuery[0]?.getBoundingClientRect();
      const rootRect = rootRef.current?.getBoundingClientRect();

      setActiveTabRect({
        height: activeRect?.height ?? 0,
        width: activeRect?.width ?? 0,
        top: (activeRect?.top ?? 0) - (rootRect?.top ?? 0),
        left: activeRect?.left ?? 0,
      });
    }
  }, [activeTabKey, windowSize, isVertical]);

  return (
    <div
      className={clsx(
        "absolute top-0 left-0 flex justify-center z-10 select-none",
        isVertical ? "flex-col w-[10vw] h-full py-14" : "w-full",
        className
      )}
      ref={rootRef}
    >
      {/* All available tabs */}
      {tabs.map(({ key, label }) => (
        <motion.button
          key={key}
          onClick={() => {
            showSlide(key);
          }}
          className={clsx(
            "truncate transition-colors flex-1 text-lg 3xl:text-xl",
            isVertical && "rotate-[-90deg]",
            key === activeTabKey && "font-bold active",
            color === "dark" ? "text-dark" : "text-light"
          )}
        >
          <div className={clsx("truncate", isVertical ? "px-6 py-2" : "py-2 px-3")}>{label}</div>
        </motion.button>
      ))}

      {/* active tab indicator: moving rounded border around active tab */}
      {activeTab && activeTabRect && (
        <div
          style={{
            transition: "all 0.3s linear",
            position: "absolute",
            height: activeTabRect.height,
            width: activeTabRect.width,
            top: activeTabRect.top,
            left: activeTabRect.left,
          }}
          className={clsx(
            "transition-colors",
            "px-2 border-2 text-center text-transparent rounded-full",
            color === "dark" ? "border-dark" : "border-light"
          )}
        />
      )}
    </div>
  );
};
